import React, { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/library";

import { isCameraPermissionDenied, isSafariUserAgent, openRearCameraFromNavigator } from "./rearCamera";
import {
  BARCODE_FRAME_SHELL,
  QR_FRAME_SHELL,
  SCANNER_CANVAS_STYLE,
  SCANNER_VIDEO_STYLE,
  ScannerMode,
  acceptsDetectorCode,
  acceptsZxingFormat,
  detectorFormats,
  paintVideoCover,
  zxingHints,
} from "./scannerProfile";

const PERMISSION_OVERLAY_STYLE = {
  alignItems: "center",
  background: "rgba(0, 0, 0, 0.82)",
  bottom: 0,
  color: "#fff",
  display: "flex",
  flexDirection: "column",
  fontSize: 14,
  gap: 12,
  justifyContent: "center",
  left: 0,
  lineHeight: "18px",
  overflowY: "auto",
  padding: 16,
  position: "absolute",
  right: 0,
  textAlign: "center",
  top: 0,
  zIndex: 2,
} as const;

const PERMISSION_BUTTON_STYLE = {
  background: "#FF7700",
  border: "none",
  borderRadius: 8,
  color: "#fff",
  fontSize: 14,
  fontWeight: 700,
  padding: "10px 16px",
} as const;

export function BarcodeScanner({
  setCode,
  open,
  setOpen,
  mode = "barcode",
  frame = "fill",
  onError,
}: {
  setCode: (code: string) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  mode?: ScannerMode;
  frame?: "fill" | "square";
  onError?: (error: unknown) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lastCodeRef = useRef<string | null>(null);
  const setCodeRef = useRef(setCode);
  const setOpenRef = useRef(setOpen);
  const onErrorRef = useRef(onError);
  const sessionRef = useRef(0);
  const streamRef = useRef<{ getTracks: () => Array<{ stop: () => void }> } | null>(null);
  const rafRef = useRef<number | null>(null);
  const [cameraBlocked, setCameraBlocked] = useState(false);
  setCodeRef.current = setCode;
  setOpenRef.current = setOpen;
  onErrorRef.current = onError;

  const releaseCamera = () => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const attachCamera = (session: number, streamPromise: Promise<{ getTracks: () => Array<{ stop: () => void }> }>) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video) return;

    const paint = () => {
      if (canvas) paintVideoCover(video, canvas);
    };
    const reader = new BrowserMultiFormatReader(zxingHints(mode));

    const emitCode = (code: string) => {
      if (lastCodeRef.current === code) return;
      lastCodeRef.current = code;
      setTimeout(() => { lastCodeRef.current = null; }, 1500);
      setCodeRef.current(code);
      setOpenRef.current(false);
    };

    const startZXingLoop = () => {
      let lastAttempt = 0;
      const tick = (time: number) => {
        if (session !== sessionRef.current) return;
        paint();
        if (time - lastAttempt >= 50) {
          lastAttempt = time;
          try {
            const result = reader.decode(video);
            if (acceptsZxingFormat(mode, result.getBarcodeFormat())) {
              emitCode(result.getText());
              return;
            }
          } catch {
            // NotFoundException on frames without a code — expected
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    };

    const startNativeLoop = (detector: {
      detect(v: HTMLVideoElement): Promise<Array<{ format?: string; rawValue: string }>>;
    }) => {
      let lastAttempt = 0;
      const schedule = () => {
        if (session === sessionRef.current) rafRef.current = requestAnimationFrame(tick);
      };
      const tick = async (time: number) => {
        if (session !== sessionRef.current) return;
        paint();
        if (time - lastAttempt >= 50) {
          lastAttempt = time;
          try {
            if (video.readyState >= 2) {
              const codes = await detector.detect(video);
              const code = codes.find((item) => acceptsDetectorCode(mode, item.format) && item.rawValue);
              if (code?.rawValue) {
                emitCode(code.rawValue);
                return;
              }
            }
          } catch {
            // Detector can fail on a single frame; keep scanning
          }
        }
        schedule();
      };
      schedule();
    };

    void (async () => {
      try {
        const stream = await streamPromise;
        if (session !== sessionRef.current) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        setCameraBlocked(false);
        video.srcObject = stream as unknown as MediaStream;
        video.muted = true;
        await video.play();
        paint();

        if (session !== sessionRef.current) return;

        if ("BarcodeDetector" in window) {
          try {
            const detector = new (window as unknown as {
              BarcodeDetector: new (options: { formats: readonly string[] }) => {
                detect(v: HTMLVideoElement): Promise<Array<{ format?: string; rawValue: string }>>;
              };
            }).BarcodeDetector({
              formats: detectorFormats(mode),
            });
            startNativeLoop(detector);
          } catch {
            startZXingLoop();
          }
        } else {
          startZXingLoop();
        }
      } catch (err) {
        if (session !== sessionRef.current) return;
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        if (isCameraPermissionDenied(err)) {
          setCameraBlocked(true);
          return;
        }
        console.error("[BarcodeScanner] erro ao iniciar câmera:", err);
        onErrorRef.current?.(err);
      }
    })();
  };

  const allowCamera = () => {
    releaseCamera();
    const session = ++sessionRef.current;
    const streamPromise = openRearCameraFromNavigator();
    attachCamera(session, streamPromise);
  };

  useEffect(() => {
    if (!open || !videoRef.current) return undefined;

    const session = ++sessionRef.current;
    const streamPromise = openRearCameraFromNavigator();
    attachCamera(session, streamPromise);

    return () => {
      sessionRef.current += 1;
      releaseCamera();
    };
  }, [mode, open]);

  return (
    <div style={frame === "square" ? QR_FRAME_SHELL : BARCODE_FRAME_SHELL}>
      <video
        ref={videoRef}
        muted
        playsInline
        style={SCANNER_VIDEO_STYLE}
      />
      <canvas ref={canvasRef} style={SCANNER_CANVAS_STYLE} />
      {cameraBlocked && (
        <div style={PERMISSION_OVERLAY_STYLE}>
          {isSafariUserAgent(navigator.userAgent) ? (
            <>
              <span>O Safari bloqueou a câmera deste site e não pergunta de novo.</span>
              <span>Toque no ícone à esquerda do endereço, abra Ajustes do Site, Câmera e escolha Permitir.</span>
              <button type="button" style={PERMISSION_BUTTON_STYLE} onClick={allowCamera}>
                Já permiti, tentar de novo
              </button>
            </>
          ) : (
            <>
              <span>Permita a câmera para continuar.</span>
              <button type="button" style={PERMISSION_BUTTON_STYLE} onClick={allowCamera}>
                Permitir câmera
              </button>
              <span>Se o pedido não aparecer, libere a câmera no cadeado do navegador.</span>
            </>
          )}
        </div>
      )}
    </div>
  );
}
